import { type RefObject, useContext, useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RawCode, StackCfKey } from '../../state/stack';
import type { ScrollTriggerConfig, TweenOptions } from './types';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ElementsTimeline } from '../../Display';
import type { PositionContextType } from '../../displayComponents/displayElements/DisplayElements';
import { IsInAuthor, type TapDirectionType } from '../../state/ui';
import { tweenLibrary } from './tweenLibrary';

gsap.registerPlugin(ScrollTrigger);

// Define tween name type based on tweenLibrary keys
type TweenName = keyof typeof tweenLibrary.element;

/**
 * Optimized animation hook with stable references to prevent recreation
 * @param ref - The ref of the element to animate.
 * @param tweenName - The name(s) of the premade tween(s) to apply, separated by spaces.
 * @param options - Optional tween customization parameters.
 * @param scrollTrigger - Optional ScrollTrigger configuration.
 */
export const useElementAnimation = ({
    ref,
    tweenName,
    options,
    scrollTrigger,
    onePercent,
    direction,
    position
}: {
    ref: RefObject<HTMLElement>,
    tweenName: string | undefined,
    options?: TweenOptions,
    scrollTrigger?: ScrollTriggerConfig,
    onePercent: { width: number, height: number },
    position: PositionContextType,
    direction: TapDirectionType
}): void => {
    const stackCfKey = useRecoilValue(StackCfKey);
    const isInAuthor = useRecoilValue(IsInAuthor);
    const elementsTimeline = useContext(ElementsTimeline);
    const rawCodeSetter = useSetRecoilState(RawCode(stackCfKey));

    // Store props in a ref to avoid recreation
    const propsRef = useRef({
        tweenName,
        options,
        scrollTrigger,
        onePercent,
        direction,
        position,
        stackCfKey,
        isInAuthor,
        elementsTimeline,
        rawCodeSetter
    });

    // Update ref on every render
    propsRef.current = {
        tweenName,
        options,
        scrollTrigger,
        onePercent,
        direction,
        position,
        stackCfKey,
        isInAuthor,
        elementsTimeline,
        rawCodeSetter
    };

    // Store animation instances to prevent recreation
    const animationRef = useRef<{
        timeline: gsap.core.Timeline | null;
        scrollTrigger: ScrollTrigger | null;
    }>({
        timeline: null,
        scrollTrigger: null
    });

    // Cleanup function
    const cleanup = () => {
        if (animationRef.current.scrollTrigger) {
            animationRef.current.scrollTrigger.kill();
            animationRef.current.scrollTrigger = null;
        }
        if (animationRef.current.timeline) {
            animationRef.current.timeline.kill();
            animationRef.current.timeline = null;
        }
    };

    // Main animation setup - only recreates when absolutely necessary
    useEffect(() => {
        const props = propsRef.current;

        // Early exit conditions
        if (props.isInAuthor) return;
        if (!ref.current) {
            console.warn("Target element not found. Ensure the ref is correctly attached.");
            return;
        }
        if (props.position === "bottom" && !props.tweenName?.includes("setImageToCover")) {
            return;
        }

        const targetElement = ref.current;
        const tweenNames = typeof props.tweenName === 'string'
            ? props.tweenName.split(' ').filter(name => name) as TweenName[]
            : [];

        if (tweenNames.length === 0) {
            return; // No tweens to apply
        }

        // Clean up previous animations
        cleanup();

        // Create new timeline
        const internalTimeline = gsap.timeline();
        animationRef.current.timeline = internalTimeline;

        let lastPosition: number | string = ">";

        // Build the timeline from specified tweens
        for (const name of tweenNames) {
            const animationFn = tweenLibrary.element[name];
            if (animationFn) {
                const animation = animationFn({
                    target: targetElement,
                    options: props.options,
                    onePercent: props.onePercent,
                    direction: props.direction
                });
                if (animation.tween) {
                    internalTimeline.add(animation.tween, animation.position ?? lastPosition);
                    lastPosition = animation.position ?? lastPosition;
                }
            } else {
                console.warn(`Tween named "${name}" not found in tweenLibrary.element.`);
            }
        }

        // Handle special "destroyFirstPage" logic
        const lastTweenName = tweenNames[tweenNames.length - 1];
        if (lastTweenName === "destroyFirstPage" && !props.isInAuthor) {
            internalTimeline.eventCallback('onComplete', () => {
                props.rawCodeSetter((prev) => {
                    const clone = structuredClone(prev);
                    if (prev.code) {
                        const firstSeparatorIndex = prev.code.indexOf("===");
                        if (firstSeparatorIndex !== -1) {
                            const secondSeparatorIndex = prev.code.indexOf("===", firstSeparatorIndex + 3);
                            if (secondSeparatorIndex !== -1) {
                                clone.code = prev.code.substring(secondSeparatorIndex + 3);
                            }
                        }
                    }
                    return clone;
                });
            });
        }

        // Integrate with ScrollTrigger or ElementsTimeline
        if (props.scrollTrigger) {
            animationRef.current.scrollTrigger = ScrollTrigger.create({
                animation: internalTimeline,
                ...props.scrollTrigger
            });
        } else if (props.elementsTimeline) {
            try {
                const finalPosition = props.options?.position ?? lastPosition;
                props.elementsTimeline.add(internalTimeline, finalPosition);
            } catch (error) {
                console.error("Failed to append to elementsTimeline:", error);
                internalTimeline.play();
            }
        } else {
            internalTimeline.play();
        }

        return cleanup;
    }, [
        // Only dependencies that REQUIRE full recreation
        tweenName,        // Different animation
        isInAuthor,       // Editor state change
        position,         // Position change
        // Note: options, onePercent, direction are accessed via ref
    ]);

    // Separate effect for updating existing animations without recreation
    useEffect(() => {
        if (!animationRef.current.timeline) return;

        // Update timeline progress or other properties without recreation
        // This is where you could handle updates that don't require full recreation
        // Props can be accessed via propsRef.current when needed

    }, [direction, onePercent]); // Updates that don't need recreation
};